import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';

interface AssessmentFiltersProps {
  searchTerm: string;
  filterCategory: string;
  filterDifficulty: string;
  categories: Array<{ _id: string; name: string; isActive: boolean }>;
  difficultyLevels: string[];
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onDifficultyChange: (value: string) => void;
  onRefresh: () => void;
}

export const AssessmentFilters: React.FC<AssessmentFiltersProps> = ({
  searchTerm,
  filterCategory,
  filterDifficulty,
  categories,
  difficultyLevels,
  onSearchChange,
  onCategoryChange,
  onDifficultyChange,
  onRefresh,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        height: '100%',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: '20px',
        backgroundColor: theme.palette.mode === 'light' 
          ? 'rgba(255, 255, 255, 0.95)' 
          : 'rgba(30, 41, 59, 0.95)',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        mb: 3,
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: theme.palette.mode === 'light' 
            ? 'rgba(255, 255, 255, 1)' 
            : 'rgba(30, 41, 59, 1)',
          boxShadow: theme.palette.mode === 'light'
            ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            : `0 12px 40px ${alpha(theme.palette.common.black, 0.5)}`,
        },
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search assessments..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  },
                  '&.Mui-focused': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => onCategoryChange(e.target.value)}
                sx={{
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={filterDifficulty}
                label="Difficulty"
                onChange={(e) => onDifficultyChange(e.target.value)}
                sx={{
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <MenuItem value="">All Levels</MenuItem>
                {difficultyLevels.map((level) => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'center' }}>
              <IconButton 
                onClick={onRefresh}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: '12px',
                  padding: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                    transform: 'rotate(180deg)'
                  }
                }}
              >
                <Refresh sx={{ color: theme.palette.primary.main }} />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};